import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CircleAlert, Info, MailWarning, ShieldAlert, UserCheck, Copy, CheckCircle2, KeyRound, ChevronLeft, ChevronRight, Wallet, RefreshCcw, MapPin, Building2, Sparkles, AlertCircle, Share2, MessageCircle, Send, XCircle, Bitcoin, Search, X as XIcon } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { ProfileShell } from "../../components/profile/ProfileShell";

const DEFAULT_CONFIG = {
  enabled: true,
  notice: "Withdrawals are processed within 24 hours.",
  currency_symbol: "৳",
  methods: [],
};

// Method logos / colors for the Quick Withdraw teaser
const METHOD_BRANDS = {
  bkash:  { logo: "/payment-logos/bkash.png",       bg: "#ffffff", pad: 2 },
  nagad:  { logo: "/payment-logos/nagad.png",       bg: "#ffffff", pad: 2 },
  rocket: { logo: "/payment-logos/rocket.png",      bg: "#ffffff", pad: 2 },
  upay:   { logo: "/payment-logos/upay.png",        bg: "#ffffff", pad: 2 },
  bank:   { logo: null, bg: "#0ea5e9", letter: "$", pad: 0 },
};

export default function WithdrawalPage() {
  const { user, refreshUser } = useAuth();
  const [view, setView] = useState("home"); // "home" | "quick" | "agent" | "crypto"
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [turnover, setTurnover] = useState(null); // {required, completed, remaining, enabled}

  const balance = Number(user?.balance || 0);
  const symbol = config.currency_symbol || "৳";
  const verificationOk = !!user?.email_verified || !!user?.phone_verified;
  const kycThreshold = 50000;
  const kycVerified = user?.kyc_status === "verified";
  const kycRequired = balance > kycThreshold;
  const allGatesOk = verificationOk && (!kycRequired || kycVerified);
  const disabled = !config.enabled;

  // First-deposit category lock
  const lockedMethod = (user?.first_deposit_method || "").toLowerCase();
  const lockedCategory = (user?.first_deposit_category || "").toLowerCase();
  const anyMethodAllowed = !!user?.allow_any_withdraw_method;
  // Agent category has its own dedicated lock (allow_non_agent_withdraw); skip here.
  const methodLockActive = !!lockedCategory && lockedCategory !== "agent" && !anyMethodAllowed;

  const CATEGORY_LABELS = {
    ewallet: {
      title: "E-Wallet only",
      methods: "bKash · Nagad · Rocket · Upay",
      cls: {
        wrap: "from-cyan-500/15 to-cyan-500/5 border-cyan-400/40",
        icon: "bg-cyan-500/20 border-cyan-400/40 text-cyan-300",
        title: "text-cyan-100",
        body: "text-cyan-200/80",
      },
    },
    crypto: {
      title: "Crypto only",
      methods: "USDT · BTC · ETH · BNB · TON · all networks",
      cls: {
        wrap: "from-amber-500/15 to-amber-500/5 border-amber-400/40",
        icon: "bg-amber-500/20 border-amber-400/40 text-amber-300",
        title: "text-amber-100",
        body: "text-amber-200/80",
      },
    },
    bank: {
      title: "Bank Transfer only",
      methods: "Bank transfer",
      cls: {
        wrap: "from-sky-500/15 to-sky-500/5 border-sky-400/40",
        icon: "bg-sky-500/20 border-sky-400/40 text-sky-300",
        title: "text-sky-100",
        body: "text-sky-200/80",
      },
    },
  };
  const lockInfo = CATEGORY_LABELS[lockedCategory] || {
    title: `${lockedMethod.toUpperCase()} only`,
    methods: lockedMethod.replace(/_/g, " ").toUpperCase(),
    cls: {
      wrap: "from-cyan-500/15 to-cyan-500/5 border-cyan-400/40",
      icon: "bg-cyan-500/20 border-cyan-400/40 text-cyan-300",
      title: "text-cyan-100",
      body: "text-cyan-200/80",
    },
  };

  // Turnover banner
  const turnoverEnabled = turnover ? turnover.enabled !== false : false;
  const turnoverRemaining = Number(turnover?.remaining || 0);
  const turnoverActive = turnoverEnabled && turnoverRemaining > 0;
  const turnoverRatio = turnover && turnover.required > 0
    ? Math.min(100, Math.round((Number(turnover.completed) / Number(turnover.required)) * 100))
    : 0;

  useEffect(() => {
    let cancelled = false;
    setLoadingCfg(true);
    api
      .get("/withdraw-config")
      .then((r) => {
        if (cancelled) return;
        setConfig({ ...DEFAULT_CONFIG, ...(r.data || {}) });
      })
      .catch(() => !cancelled && setConfig(DEFAULT_CONFIG))
      .finally(() => !cancelled && setLoadingCfg(false));

    // Fetch live turnover progress for the banner
    api
      .get("/profile/turnover")
      .then((r) => {
        if (cancelled) return;
        const t = r?.data || {};
        const required = Number(t.turnover_required ?? 0);
        const completed = Number(t.turnover_completed ?? 0);
        const remaining = Number(t.turnover_remaining ?? Math.max(0, required - completed));
        const enabled = t.turnover_enabled !== false;
        setTurnover({ required, completed, remaining, enabled });
      })
      .catch(() => { /* silent — banner just won't show */ });

    return () => { cancelled = true; };
  }, []);

  const methods = (config.methods || []).filter((m) => m.enabled !== false);

  return (
    <ProfileShell title="Withdraw">
      <div className="py-2 pb-10 space-y-4" data-testid="withdraw-page">
        {/* Premium balance card */}
        <div
          className="relative rounded-2xl p-4 sm:p-5 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(25,165,92,0.22) 0%, rgba(10,17,25,0.88) 60%)",
            border: "1px solid rgba(25,165,92,0.32)",
            boxShadow: "0 24px 60px -36px rgba(33,196,110,0.55), 0 1px 0 rgba(255,255,255,0.04) inset",
          }}
        >
          <div className="absolute inset-0 pointer-events-none opacity-50" style={{ backgroundImage: "radial-gradient(circle at 12% 0%, rgba(33,196,110,0.32), transparent 50%), radial-gradient(circle at 95% 100%, rgba(245,196,81,0.12), transparent 50%)" }} />
          <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
          <div className="relative">
            <div className="text-emerald-300/85 text-[10px] sm:text-[11px] uppercase tracking-[2.2px] font-bold inline-flex items-center gap-1.5">
              <Wallet size={11} className="text-emerald-300/80" /> Withdrawable
            </div>
            <div className="text-3xl sm:text-4xl font-black tabular-nums mt-1.5 tracking-tight" style={{ textShadow: "0 0 28px rgba(33,196,110,0.25)" }}>{symbol}{balance.toLocaleString()}</div>
          </div>
        </div>

        {/* Verification gates always visible */}
        {disabled && (
          <div data-testid="withdraw-disabled-banner" className="rounded-2xl px-4 py-3 flex items-start gap-2.5 bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm">
            <CircleAlert className="h-4 w-4 mt-0.5 shrink-0" />
            Withdrawals are temporarily disabled. Please check back later.
          </div>
        )}
        {!verificationOk && !disabled && (
          <div data-testid="withdraw-verify-banner" className="rounded-2xl px-4 py-4 bg-gradient-to-br from-amber-500/15 to-amber-500/5 border border-amber-400/40">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-amber-500/20 border border-amber-400/40 flex items-center justify-center shrink-0">
                <MailWarning className="h-5 w-5 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-amber-100 font-semibold text-sm">Verify your email or phone to withdraw</div>
                <div className="text-amber-200/80 text-xs mt-1 leading-relaxed">For your security, we need to verify at least one contact channel before processing any withdrawal.</div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Link to="/profile/email-verify" data-testid="withdraw-verify-email-cta" className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-lg bg-amber-400 hover:bg-amber-300 text-[#1a1208] text-xs font-bold transition-colors">Verify email →</Link>
                  <Link to="/profile/phone-verify" data-testid="withdraw-verify-phone-cta" className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-lg bg-white/[0.08] border border-white/15 hover:bg-white/[0.14] text-amber-100 text-xs font-bold transition-colors">Verify phone →</Link>
                </div>
              </div>
            </div>
          </div>
        )}
        {verificationOk && kycRequired && !kycVerified && !disabled && (
          <div data-testid="withdraw-kyc-banner" className="rounded-2xl px-4 py-4 bg-gradient-to-br from-rose-500/15 to-rose-500/5 border border-rose-400/40">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-rose-500/20 border border-rose-400/40 flex items-center justify-center shrink-0">
                <ShieldAlert className="h-5 w-5 text-rose-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-rose-100 font-semibold text-sm">KYC verification required</div>
                <div className="text-rose-200/80 text-xs mt-1 leading-relaxed">Your balance is over {symbol}{kycThreshold.toLocaleString()}. Please complete identity (KYC) verification before withdrawing.</div>
                <Link to="/profile/kyc" data-testid="withdraw-kyc-cta" className="inline-flex items-center gap-1.5 mt-3 px-3.5 h-9 rounded-lg bg-rose-400 hover:bg-rose-300 text-[#1a0810] text-xs font-bold transition-colors">Complete KYC →</Link>
              </div>
            </div>
          </div>
        )}

        {/* Turnover progress banner — visible whenever there's wagering remaining */}
        {turnoverActive && !disabled && (
          <div data-testid="withdraw-turnover-banner" className="rounded-2xl px-3 py-3 sm:px-4 sm:py-4 bg-gradient-to-br from-violet-500/15 to-fuchsia-500/5 border border-violet-400/40">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg bg-violet-500/20 border border-violet-400/40 flex items-center justify-center shrink-0">
                <RefreshCcw className="h-4 w-4 sm:h-5 sm:w-5 text-violet-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-violet-100 font-semibold text-[13px] sm:text-sm leading-snug">Turnover requirement not met</div>
                  <div className="text-violet-200 text-[11px] font-bold tabular-nums shrink-0">{turnoverRatio}%</div>
                </div>
                <div className="text-violet-200/80 text-[11.5px] sm:text-xs leading-relaxed">
                  Withdraw করার আগে আরও <b className="text-white">{symbol}{turnoverRemaining.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b> turnover (wager) সম্পন্ন করতে হবে।
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-2 rounded-full bg-violet-900/50 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-violet-400 to-fuchsia-400 transition-all"
                    style={{ width: `${turnoverRatio}%` }}
                    data-testid="withdraw-turnover-progress"
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] tabular-nums">
                  <span className="text-violet-200/80">
                    Done: <b className="text-emerald-300">{symbol}{Number(turnover.completed || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                  </span>
                  <span className="text-violet-200/40">·</span>
                  <span className="text-violet-200/80">
                    Required: <b className="text-violet-100">{symbol}{Number(turnover.required || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                  </span>
                  <Link
                    to="/profile/turnover"
                    data-testid="withdraw-turnover-cta"
                    className="ml-auto inline-flex items-center gap-1 px-2.5 h-7 rounded-md bg-violet-400 hover:bg-violet-300 text-[#1d0a3b] text-[11px] font-bold transition-colors"
                  >
                    Details →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* First-deposit category lock notice */}
        {methodLockActive && !disabled && (
          <div data-testid="withdraw-method-lock-banner" className={`rounded-2xl px-3 py-3 sm:px-4 sm:py-4 bg-gradient-to-br ${lockInfo.cls.wrap} border`}>
            <div className="flex items-start gap-2.5 sm:gap-3">
              <div className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg flex items-center justify-center shrink-0 border ${lockInfo.cls.icon}`}>
                <Info className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-[13px] sm:text-sm leading-snug ${lockInfo.cls.title} break-words`}>
                  {lockInfo.title}
                  <span className="block text-white/60 font-normal text-[11px] sm:text-xs mt-0.5">{lockInfo.methods}</span>
                </div>
                <div className={`text-[11.5px] sm:text-xs mt-1.5 leading-relaxed ${lockInfo.cls.body}`}>
                  {lockedCategory === "ewallet" && (
                    <>আপনি প্রথমে <b className="text-white">e-wallet</b>-এ deposit করেছিলেন, তাই শুধু <b className="text-white">bKash / Nagad / Rocket / Upay</b>-এ withdraw করতে পারবেন।</>
                  )}
                  {lockedCategory === "crypto" && (
                    <>আপনি প্রথমে <b className="text-white">crypto</b>-তে deposit করেছিলেন, তাই যেকোনো <b className="text-white">crypto network (USDT / BTC / ETH / BNB / TON ইত্যাদি)</b>-এ withdraw করতে পারবেন।</>
                  )}
                  {lockedCategory === "bank" && (
                    <>আপনি প্রথমে <b className="text-white">bank transfer</b>-এ deposit করেছিলেন, তাই শুধু <b className="text-white">bank transfer</b>-এ withdraw করতে পারবেন।</>
                  )}
                  {!["ewallet", "crypto", "bank"].includes(lockedCategory) && (
                    <>নিরাপত্তার জন্য আপনি প্রথমে যে category-তে deposit করেছিলেন, একই category-তে withdraw করতে হবে।</>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Home view: 2 big cards */}
        {view === "home" && (
          <div className="space-y-3" data-testid="withdraw-home">
            {/* Quick Withdraw card */}
            <button
              type="button"
              data-testid="withdraw-card-quick"
              onClick={() => setView("quick")}
              disabled={disabled}
              className="w-full rounded-2xl p-4 bg-[#16202d] hover:bg-[#1a2635] border border-white/[0.06] hover:border-emerald-500/40 transition-all text-left flex items-center gap-3 disabled:opacity-50"
            >
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 grid place-items-center shrink-0">
                <RefreshCcw className="h-5 w-5 text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-base font-extrabold leading-tight">Quick Withdraw</div>
                <div className="text-white/50 text-[12px] mt-0.5">Direct to bKash, Nagad & Rocket</div>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 mr-2">
                {methods.slice(0, 3).map((m) => (
                  <MethodBrand key={m.id} method={m} />
                ))}
              </div>
              <ChevronRight className="text-white/40 shrink-0" size={20} />
            </button>

            {/* Withdraw By Agent card */}
            <button
              type="button"
              data-testid="withdraw-card-agent"
              onClick={() => setView("agent")}
              disabled={disabled}
              className="w-full rounded-2xl p-4 bg-[#16202d] hover:bg-[#1a2635] border border-white/[0.06] hover:border-emerald-500/40 transition-all text-left flex items-center gap-3 disabled:opacity-50"
            >
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 grid place-items-center shrink-0">
                <UserCheck className="h-5 w-5 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-base font-extrabold leading-tight">Withdraw By Agent</div>
                <div className="text-white/50 text-[12px] mt-0.5">Pick city + agent, get a 6-digit code, collect cash offline</div>
              </div>
              <ChevronRight className="text-white/40 shrink-0" size={20} />
            </button>

            {/* Crypto Withdraw card */}
            <button
              type="button"
              data-testid="withdraw-card-crypto"
              onClick={() => setView("crypto")}
              disabled={disabled}
              className="w-full rounded-2xl p-4 bg-[#16202d] hover:bg-[#1a2635] border border-white/[0.06] hover:border-amber-500/40 transition-all text-left flex items-center gap-3 disabled:opacity-50"
            >
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500/20 via-amber-500/10 to-emerald-500/10 border border-amber-500/30 grid place-items-center shrink-0">
                <Bitcoin className="h-5 w-5 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-base font-extrabold leading-tight">Crypto Withdraw</div>
                <div className="text-white/50 text-[12px] mt-0.5">USDT (TRC-20 / BEP-20), USDC, BNB, ETH, TON</div>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 mr-2">
                <CryptoBadge logo="/payment-logos/usdt.svg" bg="#26a17b" />
                <CryptoBadge logo="/payment-logos/btc.svg"  bg="#f7931a" />
                <CryptoBadge logo="/payment-logos/eth.svg"  bg="#627eea" />
              </div>
              <ChevronRight className="text-white/40 shrink-0" size={20} />
            </button>
          </div>
        )}

        {view === "quick" && (
          <QuickWithdrawForm
            config={config}
            loadingCfg={loadingCfg}
            disabled={disabled}
            allGatesOk={allGatesOk}
            verificationOk={verificationOk}
            kycRequired={kycRequired}
            kycVerified={kycVerified}
            balance={balance}
            symbol={symbol}
            onBack={() => setView("home")}
            onAfterSubmit={refreshUser}
          />
        )}

        {view === "agent" && (
          <AgentWithdrawForm
            disabled={disabled}
            allGatesOk={allGatesOk}
            verificationOk={verificationOk}
            kycRequired={kycRequired}
            kycVerified={kycVerified}
            balance={balance}
            symbol={symbol}
            onBack={() => setView("home")}
            onAfterSubmit={refreshUser}
          />
        )}

        {view === "crypto" && (
          <CryptoWithdrawForm
            disabled={disabled}
            allGatesOk={allGatesOk}
            verificationOk={verificationOk}
            kycRequired={kycRequired}
            kycVerified={kycVerified}
            balance={balance}
            symbol={symbol}
            onBack={() => setView("home")}
            onAfterSubmit={refreshUser}
          />
        )}

        {/* Notice */}
        {view !== "home" && (
          <div className="rounded-2xl px-4 py-3 flex items-start gap-2.5 bg-white/[0.03] border border-white/10 text-xs text-white/55 leading-relaxed">
            <Info className="h-4 w-4 mt-0.5 text-white/45 shrink-0" />
            <span data-testid="withdraw-notice">
              {view === "agent"
                ? "Withdraw by Agent: balance is debited immediately, agent enters your 6-digit code to release cash. Rejected requests are auto-refunded."
                : (config.notice || "Withdrawals are processed within 24 hours.")}
            </span>
          </div>
        )}
      </div>
    </ProfileShell>
  );
}

function MethodBrand({ method }) {
  const id = (method?.id || "").toLowerCase();
  const brand = METHOD_BRANDS[id] || { bg: "#19a55c", letter: (method?.name || "?")[0], pad: 0 };
  return (
    <div
      className="h-9 w-9 rounded-xl flex items-center justify-center overflow-hidden shadow-md"
      style={{ background: brand.bg, padding: brand.pad ?? 4, border: brand.bg === "#ffffff" ? "1px solid rgba(0,0,0,0.06)" : "none" }}
      title={method?.name}
      data-testid={`withdraw-brand-${id}`}
    >
      {brand.logo ? (
        <img src={brand.logo} alt={method?.name || id} className="h-full w-full object-contain" draggable={false} />
      ) : (
        <span className="text-white text-[12px] font-extrabold">{brand.letter}</span>
      )}
    </div>
  );
}

// ============================================================
// Quick Withdraw form (extracted)
// ============================================================
function QuickWithdrawForm({ config, loadingCfg, disabled, allGatesOk, verificationOk, kycRequired, kycVerified, balance, symbol, onBack, onAfterSubmit }) {
  const [methodId, setMethodId] = useState("");
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const methods = useMemo(
    () => (config.methods || []).filter((m) => m.enabled !== false && m.id !== "bank"),
    [config.methods],
  );

  useEffect(() => {
    if (!methodId && methods.length) setMethodId(methods[0].id);
  }, [methods, methodId]);

  const activeMethod = useMemo(() => methods.find((m) => m.id === methodId) || null, [methods, methodId]);
  const minAmt = Number(activeMethod?.min || 0);
  const maxAmt = Number(activeMethod?.max || 0);
  const feePct = Number(activeMethod?.fee_pct || 0);
  const amt = Number(amount) || 0;
  const feeAmt = amt > 0 ? +(amt * feePct / 100).toFixed(2) : 0;
  const payoutAmt = Math.max(0, amt - feeAmt);

  const submit = async (e) => {
    e.preventDefault();
    if (!config.enabled) return toast.error("Withdrawals are currently disabled");
    if (!verificationOk) return toast.error("Please verify your email or phone before withdrawing");
    if (kycRequired && !kycVerified) return toast.error(`KYC verification is required for withdrawals when balance exceeds ${symbol}50,000`);
    if (!activeMethod) return toast.error("Select a withdrawal method");
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (minAmt && amt < minAmt) return toast.error(`Minimum for ${activeMethod.name} is ${symbol}${minAmt.toLocaleString()}`);
    if (maxAmt && amt > maxAmt) return toast.error(`Maximum for ${activeMethod.name} is ${symbol}${maxAmt.toLocaleString()}`);
    if (amt > balance) return toast.error("Insufficient balance");
    if (!account.trim()) return toast.error(`Enter your ${activeMethod.account_label || "account number"}`);

    setSubmitting(true);
    try {
      await api.post("/profile/wallet/withdraw", { amount: amt, method: activeMethod.id, payer_account: account });
      await onAfterSubmit?.();
      toast.success("Withdrawal request submitted");
      setAmount(""); setAccount("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Withdrawal failed");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 bg-[#0e1622] border border-white/8 rounded-2xl p-4" data-testid="quick-withdraw-form">
      <BackHeader title="Quick Withdraw" sub="Direct to bKash, Nagad & Rocket" onBack={onBack} icon={Wallet} color="#10b981" />

      {loadingCfg ? (
        <div className="h-11 rounded-lg bg-[#101824] border border-white/10 px-3 flex items-center text-white/50 text-sm">Loading methods…</div>
      ) : methods.length === 0 ? (
        <div data-testid="withdraw-no-methods" className="rounded-2xl px-4 py-3 bg-amber-500/5 border border-amber-500/30 text-amber-200 text-sm flex items-start gap-2">
          <CircleAlert className="h-4 w-4 mt-0.5 shrink-0" /> No withdrawal methods are currently active.
        </div>
      ) : (
        <div>
          <div className="text-xs uppercase tracking-wider text-white/55 mb-2">Method</div>
          <div className="grid grid-cols-3 gap-2.5">
            {methods.map((m) => {
              const brand = METHOD_BRANDS[m.id] || { logo: null, bg: "#19a55c", pad: 0 };
              const selected = methodId === m.id;
              return (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => setMethodId(m.id)}
                  data-testid={`wmethod-${m.id}`}
                  className={`relative rounded-xl border transition-all flex flex-col items-center justify-center gap-1.5 py-3 px-2 ${
                    selected
                      ? "border-[#19a55c] bg-[#19a55c]/10 ring-1 ring-[#19a55c]/40"
                      : "border-white/10 bg-[#101824] hover:bg-white/[0.04] hover:border-white/20"
                  }`}
                >
                  {brand.logo ? (
                    <img
                      src={brand.logo}
                      alt={m.name}
                      className="h-10 w-10 object-contain rounded-lg shrink-0"
                      draggable={false}
                    />
                  ) : brand.letter ? (
                    <span
                      className="inline-flex items-center justify-center h-10 w-10 rounded-lg text-white text-base font-extrabold shrink-0"
                      style={{ background: brand.bg }}
                    >
                      {brand.letter}
                    </span>
                  ) : null}
                  <span className={`text-[13px] font-semibold truncate w-full text-center ${selected ? "text-white" : "text-white/80"}`}>
                    {m.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {activeMethod && (
        <>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/55">{activeMethod.account_label || "Account number"}</span>
            <input type="text" data-testid="withdraw-account" value={account} onChange={(e) => setAccount(e.target.value)} placeholder={`Enter your ${(activeMethod.account_label || "account number").toLowerCase()}`} disabled={disabled}
              className="mt-1 w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-white placeholder:text-white/30 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/25 disabled:opacity-60" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/55 flex items-center justify-between">
              <span>Amount (BDT)</span>
              <span className="text-white/40 normal-case tracking-normal text-[11px]">Min {symbol}{minAmt.toLocaleString()}{maxAmt ? ` · Max ${symbol}${maxAmt.toLocaleString()}` : ""}</span>
            </span>
            <input type="number" inputMode="numeric" data-testid="withdraw-amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" disabled={disabled}
              className="mt-1 w-full h-12 rounded-lg bg-[#101824] border border-white/10 px-4 text-white text-lg font-bold tabular-nums focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/25 disabled:opacity-60" />
          </label>

          {feePct > 0 && amt > 0 && (
            <div data-testid="withdraw-fee-preview" className="rounded-xl px-3 py-2.5 bg-white/[0.03] border border-white/10 text-xs text-white/70 flex items-center justify-between">
              <span>Fee ({feePct}%)</span>
              <span className="font-semibold">−{symbol}{feeAmt.toLocaleString()} · Payout {symbol}{payoutAmt.toLocaleString()}</span>
            </div>
          )}
        </>
      )}

      <button type="submit" data-testid="withdraw-submit" disabled={submitting || disabled || !activeMethod || !allGatesOk}
        className="w-full h-12 rounded-lg text-white font-extrabold tracking-wide border border-white/15 bg-white/[0.04] hover:bg-white/10 active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
        {submitting ? "Processing…" : !verificationOk ? "Verify email or phone to withdraw" : kycRequired && !kycVerified ? "Complete KYC to withdraw" : "Request withdrawal"}
      </button>
    </form>
  );
}

// ============================================================
// Withdraw by Agent — City → State (agent) → Amount → Code
// ============================================================
function AgentWithdrawForm({ disabled, allGatesOk, verificationOk, kycRequired, kycVerified, balance, symbol, onBack, onAfterSubmit }) {
  const [cities, setCities] = useState([]); // [{city, has_agent}]
  const [city, setCity] = useState("");
  const [agentsInCity, setAgentsInCity] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  useEffect(() => {
    api.get("/agents/cities").then((r) => setCities(r.data?.items || [])).catch(() => setCities([]));
    api.get("/profile/agent-withdrawals?limit=10").then((r) => setHistory(r.data?.items || [])).catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    if (!city) { setAgentsInCity([]); setAgentId(""); return; }
    setLoadingAgents(true);
    api.get(`/agents/by-city?city=${encodeURIComponent(city)}`)
      .then((r) => {
        const items = r.data?.items || [];
        setAgentsInCity(items);
        setAgentId(items.length === 1 ? items[0].id : "");
      })
      .catch(() => setAgentsInCity([]))
      .finally(() => setLoadingAgents(false));
  }, [city]);

  const activeAgent = agentsInCity.find((a) => a.id === agentId) || null;
  const cityHasAgents = cities.find((c) => c.city === city)?.has_agent;
  const noAgentsInCity = !!city && !loadingAgents && agentsInCity.length === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!verificationOk) return toast.error("Please verify your email or phone before withdrawing");
    if (kycRequired && !kycVerified) return toast.error(`KYC verification is required when balance exceeds ${symbol}50,000`);
    if (!city) return toast.error("Select a City");
    if (!agentId) return toast.error("Select an Agent State (address)");
    const a = Number(amount) || 0;
    if (!a || a <= 0) return toast.error("Enter a valid amount");
    if (a > balance) return toast.error("Insufficient balance");

    setSubmitting(true);
    try {
      const { data } = await api.post("/profile/wallet/withdraw-by-agent", {
        agent_id: agentId,
        amount: a,
        account_info: activeAgent ? `${activeAgent.city} — ${activeAgent.state}` : "",
      });
      setReceipt({
        code: data.code,
        amount: data.amount,
        agent_username: data.agent.username,
        agent_name: data.agent.name,
        city: activeAgent?.city,
        state: activeAgent?.state,
      });
      await onAfterSubmit?.();
      setAmount("");
      api.get("/profile/agent-withdrawals?limit=10").then((r) => setHistory(r.data?.items || [])).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Withdrawal request failed");
    } finally { setSubmitting(false); }
  };

  const copyCode = async (code) => {
    const ok = await copyToClipboard(String(code ?? ""));
    if (ok) toast.success("Code copied");
    else toast.error(`Copy blocked — please copy manually: ${code}`);
  };

  const quickAmounts = [500, 1000, 2000, 5000].filter((v) => v <= balance);
  const stepNum = !city ? 1 : !agentId ? 2 : !amount ? 3 : 4;

  const [cancelConfirmId, setCancelConfirmId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const cancelPending = async (h) => {
    // Two-tap confirm: first click flips the button, second click actually cancels.
    if (cancelConfirmId !== h.id) {
      setCancelConfirmId(h.id);
      // auto-revert after 4s if user doesn't confirm
      setTimeout(() => {
        setCancelConfirmId((cur) => (cur === h.id ? null : cur));
      }, 4000);
      return;
    }
    setCancelling(true);
    try {
      const { data } = await api.post(`/profile/agent-withdrawals/${h.id}/cancel`);
      toast.success(`Cancelled · ${symbol}${Number(data.refunded_amount).toLocaleString()} refunded`);
      await onAfterSubmit?.();
      api.get("/profile/agent-withdrawals?limit=10").then((r) => setHistory(r.data?.items || [])).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cancel failed");
    } finally {
      setCancelling(false);
      setCancelConfirmId(null);
    }
  };

  return (
    <div className="space-y-4">
      <style>{`
        @keyframes wba-rise { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: none; } }
        .wba-rise { animation: wba-rise .45s cubic-bezier(.2,.8,.2,1) both; }
        @keyframes wba-shimmer { 0%,100% { opacity: .4; } 50% { opacity: .9; } }
        .wba-shimmer { animation: wba-shimmer 2.4s ease-in-out infinite; }
        @keyframes wba-receipt-pop { 0% { opacity: 0; transform: scale(.96); } 100% { opacity: 1; transform: scale(1); } }
        .wba-receipt-pop { animation: wba-receipt-pop .55s cubic-bezier(.2,.8,.2,1) both; }
      `}</style>

      <div
        className="relative overflow-hidden rounded-2xl wba-rise"
        style={{
          background: "linear-gradient(160deg,#0e1622 0%,#101a25 60%,#1a1408 110%)",
          border: "1px solid rgba(245,158,11,0.18)",
          boxShadow: "0 24px 60px -36px rgba(245,158,11,0.55), 0 1px 0 rgba(255,255,255,0.04) inset",
        }}
        data-testid="agent-withdraw-form"
      >
        {/* Ambient glows */}
        <div className="absolute inset-0 pointer-events-none opacity-50" style={{ backgroundImage: "radial-gradient(circle at 12% 0%, rgba(245,158,11,0.18), transparent 45%), radial-gradient(circle at 90% 100%, rgba(33,196,110,0.10), transparent 45%)" }} />
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

        <div className="relative p-4 sm:p-5">
          {/* Header with back button & step indicator */}
          <div className="flex items-center gap-3 mb-1">
            <button type="button" onClick={onBack} data-testid="withdraw-back-button" className="h-9 w-9 grid place-items-center rounded-lg bg-white/[0.05] hover:bg-white/10 border border-white/10 text-white/70 transition">
              <ChevronLeft size={16} />
            </button>
            <div className="h-10 w-10 grid place-items-center rounded-xl shrink-0" style={{ background: "linear-gradient(135deg,#f59e0b,#f59e0b88)", boxShadow: "0 10px 22px -10px rgba(245,158,11,0.7)" }}>
              <UserCheck size={18} className="text-[#1a1208]" strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-[16px] sm:text-[17px] font-extrabold leading-tight tracking-tight">Withdraw By Agent</div>
              <div className="text-white/55 text-[11.5px] truncate">Pick city, choose agent address, get a 6-digit code</div>
            </div>
          </div>

          {/* Step indicator with labels */}
          <div className="mt-4 mb-5">
            <div className="flex items-center gap-1 sm:gap-2">
              {[
                { n: 1, label: "City", Icon: Building2 },
                { n: 2, label: "Agent", Icon: MapPin },
                { n: 3, label: "Amount", Icon: Wallet },
                { n: 4, label: "Code", Icon: KeyRound },
              ].map(({ n, label, Icon }, i) => {
                const done = stepNum > n;
                const active = stepNum === n;
                return (
                  <div key={n} className="flex items-center flex-1 min-w-0">
                    <div className={`flex flex-col items-center gap-1 flex-1 transition-all ${done || active ? "" : "opacity-40"}`}>
                      <div
                        className="h-8 w-8 sm:h-9 sm:w-9 rounded-full grid place-items-center transition-all relative"
                        style={{
                          background: done
                            ? "linear-gradient(135deg,#21c46e,#19a55c)"
                            : active
                            ? "linear-gradient(135deg,#f59e0b,#fbbf24)"
                            : "rgba(255,255,255,0.05)",
                          border: active ? "2px solid rgba(245,158,11,0.5)" : done ? "2px solid rgba(33,196,110,0.4)" : "2px solid rgba(255,255,255,0.08)",
                          boxShadow: active ? "0 0 0 4px rgba(245,158,11,0.12), 0 8px 18px -8px rgba(245,158,11,0.6)" : done ? "0 6px 16px -8px rgba(33,196,110,0.6)" : "none",
                        }}
                      >
                        {done ? (
                          <CheckCircle2 size={14} className="text-[#06140b]" strokeWidth={3} />
                        ) : (
                          <Icon size={13} className={active ? "text-[#1a1208]" : "text-white/50"} strokeWidth={2.5} />
                        )}
                      </div>
                      <span className={`text-[9.5px] sm:text-[10.5px] font-bold uppercase tracking-[1.2px] transition ${active ? "text-amber-300" : done ? "text-emerald-300" : "text-white/40"}`}>{label}</span>
                    </div>
                    {i < 3 && (
                      <div className="h-[2px] flex-1 max-w-[44px] -mt-5 rounded-full transition-all"
                        style={{
                          background: stepNum > n ? "linear-gradient(90deg,#19a55c,#21c46e)" : "rgba(255,255,255,0.06)",
                        }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {receipt && (
            <div
              className="mb-4 wba-receipt-pop relative"
              data-testid="agent-withdraw-receipt"
            >
              {/* Ticket-style card with perforated edges */}
              <div className="relative rounded-2xl overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, rgba(33,196,110,0.16) 0%, rgba(10,17,25,0.95) 60%, rgba(33,196,110,0.10) 100%)",
                  border: "1px solid rgba(33,196,110,0.45)",
                  boxShadow: "0 30px 70px -28px rgba(33,196,110,0.7)",
                }}
              >
                {/* Confetti dots */}
                <div className="absolute inset-0 pointer-events-none opacity-30" style={{ backgroundImage: "radial-gradient(circle at 100% 0%, rgba(33,196,110,0.45), transparent 50%), radial-gradient(circle at 0% 100%, rgba(245,158,11,0.18), transparent 50%)" }} />
                {/* Top section */}
                <div className="relative p-4 sm:p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="relative">
                      <div className="h-11 w-11 rounded-xl bg-emerald-500/30 grid place-items-center shrink-0 border border-emerald-400/50 wba-shimmer" style={{ boxShadow: "0 0 24px rgba(33,196,110,0.6)" }}>
                        <CheckCircle2 className="text-emerald-300" size={22} strokeWidth={2.5} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <div className="font-black text-emerald-100 text-[15px] sm:text-[16px] tracking-tight">Code generated · Share it offline</div>
                      <div className="text-emerald-300/80 text-[11.5px] mt-1 leading-relaxed">
                        Show this to <span className="font-bold text-white">{receipt.agent_name}</span> at <b>{receipt.city} — {receipt.state}</b> to collect cash.
                      </div>
                    </div>
                  </div>

                  {/* The big code display */}
                  <div className="relative rounded-xl overflow-hidden"
                    style={{
                      background: "linear-gradient(135deg,#021610 0%,#06241a 100%)",
                      border: "1px solid rgba(33,196,110,0.30)",
                    }}
                  >
                    <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(33,196,110,0.12) 1px,transparent 1px)", backgroundSize: "100% 22px" }} />
                    <div className="relative p-4 sm:p-5">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="text-[10px] uppercase tracking-[2.5px] text-emerald-300/65 font-bold inline-flex items-center gap-1.5">
                          <KeyRound size={10} /> Withdrawal Code
                        </div>
                        <div className="text-right">
                          <div className="text-[9px] uppercase tracking-[2px] text-emerald-300/65 font-bold">Amount</div>
                          <div className="text-[18px] sm:text-[20px] font-extrabold text-white tabular-nums leading-none mt-0.5">{symbol}{Number(receipt.amount).toLocaleString()}</div>
                        </div>
                      </div>
                      {/* Code in 6 separated boxes */}
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-2" data-testid="agent-withdraw-code">
                        {String(receipt.code).split("").map((ch, idx) => (
                          <div
                            key={idx}
                            className="flex-1 aspect-[3/4] max-w-[64px] rounded-lg grid place-items-center font-mono font-black text-[28px] sm:text-[34px] text-emerald-300 tabular-nums"
                            style={{
                              background: "linear-gradient(180deg,rgba(33,196,110,0.10) 0%,rgba(0,0,0,0.30) 100%)",
                              border: "1px solid rgba(33,196,110,0.35)",
                              boxShadow: "inset 0 0 16px rgba(33,196,110,0.15), 0 8px 20px -8px rgba(0,0,0,0.6)",
                              textShadow: "0 0 18px rgba(33,196,110,0.7)",
                              animation: `wba-rise .35s cubic-bezier(.2,.8,.2,1) ${idx * 60}ms both`,
                            }}
                          >
                            {ch}
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-[10.5px] text-emerald-300/55 inline-flex items-center gap-1.5 font-medium">
                        <AlertCircle size={11} /> One-time use · valid until the agent approves or rejects
                      </div>
                    </div>
                  </div>

                  {/* Action buttons grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                    <button
                      onClick={() => copyCode(receipt.code)}
                      className="h-10 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-100 rounded-lg text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5 transition"
                      data-testid="agent-withdraw-copy"
                    >
                      <Copy size={12} /> Copy
                    </button>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`My withdrawal code from Maxlive88: ${receipt.code} (Amount: ${symbol}${Number(receipt.amount).toLocaleString()})`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-10 bg-[#25d366]/15 hover:bg-[#25d366]/25 border border-[#25d366]/40 text-[#7ee8a4] rounded-lg text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5 transition"
                      data-testid="agent-withdraw-whatsapp"
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                    <a
                      href={`https://t.me/share/url?url=${encodeURIComponent("https://maxlive88.com")}&text=${encodeURIComponent(`My withdrawal code: ${receipt.code} (${symbol}${Number(receipt.amount).toLocaleString()})`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-10 bg-[#0088cc]/15 hover:bg-[#0088cc]/25 border border-[#0088cc]/40 text-[#7cc8eb] rounded-lg text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5 transition"
                      data-testid="agent-withdraw-telegram"
                    >
                      <Send size={12} /> Telegram
                    </a>
                    <button
                      onClick={() => setReceipt(null)}
                      className="h-10 bg-white/[0.06] hover:bg-white/10 border border-white/10 text-white/70 rounded-lg text-[11.5px] font-bold transition"
                      data-testid="agent-withdraw-dismiss"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>

                {/* Perforated separator */}
                <div className="relative h-3" aria-hidden>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-[#070d18] -ml-2.5 border border-emerald-500/45" />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-[#070d18] -mr-2.5 border border-emerald-500/45" />
                  <div className="absolute left-3 right-3 top-1/2 -translate-y-1/2 border-t border-dashed border-emerald-500/30" />
                </div>

                {/* Bottom stub: agent contact */}
                {receipt.agent_username && (
                  <div className="relative px-4 sm:px-5 py-3 flex items-center justify-between gap-3 bg-emerald-500/5">
                    <div className="text-[10.5px] text-white/55 inline-flex items-center gap-1.5 truncate">
                      <UserCheck size={11} className="text-amber-300" />
                      <span>Agent: <span className="font-mono text-amber-300">@{receipt.agent_username}</span></span>
                    </div>
                    <div className="text-[10.5px] text-white/40 font-mono shrink-0">
                      {new Date().toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-3.5">
            {/* === Step 1: Agent City === */}
            <div>
              <label className="text-[10px] sm:text-[11px] uppercase tracking-[1.6px] text-white/55 inline-flex items-center gap-1.5 font-bold">
                <Building2 size={12} className="text-amber-400/80" /> Agent City
                <span className="text-white/30 font-normal normal-case tracking-normal text-[10.5px] ml-1">(64 districts)</span>
              </label>
              <div className="relative mt-1.5">
                <SearchableSelect
                  options={cities}
                  value={city}
                  onChange={(v) => setCity(v)}
                  getValue={(o) => o.city}
                  getLabel={(o) => o.city}
                  getSearchText={(o) => o.city}
                  placeholder="— Select City —"
                  searchPlaceholder="Type city name…"
                  emptyText="No matching district"
                  leftIcon={Building2}
                  testid="agent-city-select"
                  searchTestid="agent-city-search"
                  listTestid="agent-city-list"
                  itemTestidPrefix="agent-city-opt"
                />
              </div>
            </div>

            {/* === Step 2: Agent State === */}
            <div>
              <label className="text-[10px] sm:text-[11px] uppercase tracking-[1.6px] text-white/55 inline-flex items-center gap-1.5 font-bold">
                <MapPin size={12} className="text-amber-400/80" /> Agent State (Address)
              </label>
              <div className="relative mt-1.5">
                <SearchableSelect
                  options={agentsInCity}
                  value={agentId}
                  onChange={(v) => setAgentId(v)}
                  getValue={(o) => o.id}
                  getLabel={(o) => o.state || "—"}
                  getSub={(o) => `${o.name || ""}${o.username ? ` · @${o.username}` : ""}`}
                  getSearchText={(o) => `${o.state || ""} ${o.name || ""} ${o.username || ""} ${o.mobile || ""}`}
                  placeholder={!city ? "Choose a city first" : loadingAgents ? "Loading agents…" : noAgentsInCity ? "No agents available" : "— Select Agent —"}
                  searchPlaceholder="Type address, name or username…"
                  emptyText="No matching agent"
                  leftIcon={MapPin}
                  disabled={!city || loadingAgents || agentsInCity.length === 0}
                  testid="agent-state-select"
                  searchTestid="agent-state-search"
                  listTestid="agent-state-list"
                  itemTestidPrefix="agent-state-opt"
                  renderRow={(o, isActive) => (
                    <div className="px-3 py-2 flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg shrink-0 grid place-items-center text-[12px] font-black"
                        style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#1a1208" }}
                      >
                        {(o.name || o.username || "A").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[13px] font-semibold truncate ${isActive ? "text-amber-200" : "text-white/90"}`}>{o.state || "—"}</div>
                        <div className="text-[11px] text-white/55 truncate">
                          {o.name || ""}
                          {o.username ? <span className="font-mono ml-1 text-amber-300/80">@{o.username}</span> : null}
                        </div>
                      </div>
                      {isActive && <CheckCircle2 size={14} className="text-amber-300 shrink-0" />}
                    </div>
                  )}
                />
              </div>

              {/* Selected agent preview card */}
              {activeAgent && (
                <div className="mt-2.5 rounded-xl overflow-hidden wba-rise relative"
                  style={{
                    background: "linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(255,255,255,0.02) 70%, rgba(33,196,110,0.06) 100%)",
                    border: "1px solid rgba(245,158,11,0.30)",
                  }}
                  data-testid="agent-preview-card"
                >
                  <div aria-hidden className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-amber-400/10 blur-2xl pointer-events-none" />
                  <div className="relative p-3.5">
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="h-12 w-12 rounded-xl grid place-items-center font-black text-[16px]" style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#1a1208", boxShadow: "0 10px 24px -8px rgba(245,158,11,0.5)" }}>
                          {(activeAgent.name || "A").charAt(0).toUpperCase()}
                        </div>
                        <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-[#0e1622]" title="Online" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-bold text-[14px] tracking-tight truncate">{activeAgent.name}</span>
                          <span className="font-mono text-[10px] text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded">@{activeAgent.username}</span>
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300/85 font-bold">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            Online
                          </span>
                        </div>
                        <div className="flex items-start gap-1.5 text-[11.5px] text-white/65 mt-1.5 font-medium leading-snug">
                          <MapPin size={11} className="text-amber-400/70 mt-0.5 shrink-0" />
                          <span className="truncate">{activeAgent.city}{activeAgent.state ? ` — ${activeAgent.state}` : ""}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* === Step 3: Amount === */}
            <div>
              <label className="text-[10px] sm:text-[11px] uppercase tracking-[1.6px] text-white/55 inline-flex items-center gap-1.5 font-bold w-full justify-between">
                <span className="inline-flex items-center gap-1.5"><Wallet size={12} className="text-amber-400/80" /> Amount</span>
                <span className="text-white/35 font-normal normal-case tracking-normal text-[10.5px]">Available: <b className="text-emerald-300">{symbol}{balance.toLocaleString()}</b></span>
              </label>
              <div className="relative mt-1.5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-xl font-bold pointer-events-none">{symbol}</span>
                <input
                  type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder="0" disabled={disabled}
                  data-testid="agent-withdraw-amount"
                  className="w-full h-14 rounded-xl bg-[#0d1521] border border-white/10 pl-10 pr-4 text-white text-2xl font-black tabular-nums focus:border-amber-500/60 focus:outline-none focus:ring-2 focus:ring-amber-500/15 disabled:opacity-60 transition"
                />
              </div>
              {/* Quick amount chips */}
              {quickAmounts.length > 0 && (
                <div className="mt-2.5 flex gap-1.5 flex-wrap">
                  {quickAmounts.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setAmount(String(q))}
                      data-testid={`quick-amount-${q}`}
                      className={`h-8 px-3 rounded-lg text-[12px] font-bold transition ${
                        Number(amount) === q
                          ? "bg-amber-500 text-[#1a1208] shadow-[0_8px_18px_-10px_rgba(245,158,11,0.7)]"
                          : "bg-white/[0.04] hover:bg-white/[0.08] text-white/70 border border-white/10"
                      }`}
                    >
                      {symbol}{q.toLocaleString()}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setAmount(String(Math.floor(balance)))}
                    data-testid="quick-amount-max"
                    className="h-8 px-3 rounded-lg text-[12px] font-bold bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 transition"
                  >
                    MAX
                  </button>
                </div>
              )}
            </div>

            {/* Info pill */}
            <div className="rounded-xl px-3.5 py-2.5 bg-emerald-500/[0.06] border border-emerald-500/20 text-[11px] text-emerald-200/95 flex items-start gap-2 leading-relaxed">
              <Sparkles size={13} className="mt-0.5 shrink-0 text-emerald-300 wba-shimmer" />
              <span>You'll receive a unique <b>6-digit code</b>. Share it with the agent at <b>{activeAgent ? `${activeAgent.city} — ${activeAgent.state}` : "the chosen address"}</b> to collect cash. Funds are debited now and held until the agent approves.</span>
            </div>

            <button
              type="submit"
              data-testid="agent-withdraw-submit"
              disabled={submitting || disabled || !allGatesOk || !city || !agentId || !amount}
              className="relative w-full h-13 sm:h-14 rounded-xl text-[#06140b] font-black tracking-wide text-[14.5px] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden group"
              style={{
                background: "linear-gradient(135deg,#21c46e 0%,#19a55c 100%)",
                boxShadow: "0 18px 36px -18px rgba(33,196,110,0.7), inset 0 1px 0 rgba(255,255,255,0.18)",
              }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "linear-gradient(135deg,rgba(255,255,255,0.18) 0%,transparent 100%)" }} />
              <span className="relative inline-flex items-center justify-center gap-2">
                {submitting ? <RefreshCcw size={15} className="animate-spin" /> : <KeyRound size={15} />}
                {submitting ? "Generating code…" : !verificationOk ? "Verify email or phone first" : kycRequired && !kycVerified ? "Complete KYC first" : "Generate withdrawal code"}
              </span>
            </button>
          </form>
        </div>
      </div>

      {history.length > 0 && (
        <div className="bg-[#0e1622] border border-white/8 rounded-2xl overflow-hidden wba-rise" style={{ animationDelay: "100ms" }} data-testid="agent-withdraw-history">
          <div className="px-4 py-3 border-b border-white/5 text-[11px] sm:text-[12px] text-white/55 font-bold uppercase tracking-[1.5px] flex items-center gap-2">
            <RefreshCcw size={11} /> Recent agent withdrawals
          </div>
          <ul className="divide-y divide-white/5 max-h-72 overflow-y-auto">
            {history.map((h, i) => (
              <li key={h.id} className="px-4 py-3 flex items-center gap-3 text-[12px] hover:bg-white/[0.02] transition wba-rise" style={{ animationDelay: `${120 + i * 30}ms` }}>
                <div className="h-8 w-8 rounded-lg bg-amber-500/10 border border-amber-500/25 grid place-items-center shrink-0">
                  <KeyRound size={13} className="text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-amber-300 tracking-[0.3em] text-[13px] font-bold">{h.code}</div>
                  <div className="text-white/45 text-[10.5px] truncate">@{h.agent_username} · {new Date(h.created_at).toLocaleDateString([], { month: "short", day: "numeric" })}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-bold text-emerald-300 tabular-nums text-[13px]">{symbol}{Number(h.amount).toLocaleString()}</div>
                  <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded-md text-[9.5px] font-black uppercase tracking-wider border ${h.status === "approved" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : h.status === "rejected" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" : h.status === "cancelled" ? "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                    {h.status}
                  </span>
                </div>
                {h.status === "pending" && (
                  <button
                    type="button"
                    onClick={() => cancelPending(h)}
                    disabled={cancelling}
                    title={cancelConfirmId === h.id ? "Tap again to confirm cancel" : "Cancel this pending withdrawal — funds refunded instantly"}
                    data-testid={`cancel-agent-withdrawal-${h.id}`}
                    className={`ml-1 h-8 px-2 inline-flex items-center gap-1 rounded-md border transition shrink-0 text-[10.5px] font-bold uppercase tracking-wider disabled:opacity-60 disabled:cursor-not-allowed ${
                      cancelConfirmId === h.id
                        ? "bg-rose-500 border-rose-400 text-white shadow-[0_0_0_3px_rgba(244,63,94,0.25)] animate-pulse"
                        : "border-rose-500/30 text-rose-300 bg-rose-500/10 hover:bg-rose-500/20"
                    }`}
                  >
                    <XCircle size={12} />
                    {cancelling && cancelConfirmId === h.id
                      ? "Cancelling…"
                      : cancelConfirmId === h.id
                      ? "Confirm?"
                      : "Cancel"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BackHeader({ title, sub, onBack, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-3 mb-4 -mt-1">
      <button type="button" onClick={onBack} data-testid="withdraw-back-button" className="h-9 w-9 grid place-items-center rounded-lg bg-white/[0.04] hover:bg-white/10 border border-white/10 text-white/70">
        <ChevronLeft size={16} />
      </button>
      <div className="h-9 w-9 grid place-items-center rounded-lg shrink-0" style={{ background: `${color}22`, border: `1px solid ${color}55`, color }}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <div className="text-white text-[15px] font-extrabold leading-tight">{title}</div>
        <div className="text-white/50 text-[11px] truncate">{sub}</div>
      </div>
    </div>
  );
}


// ============================================================
// Crypto Withdraw form — USDT / USDC / TRX / BNB / ETH / TON
// ============================================================
const CRYPTO_COINS = [
  { id: "usdt_trc20",   name: "USDT",  network: "TRC-20",  color: "#26a17b", logo: "/payment-logos/usdt.svg", min_usd: 5,  netColor: "#ef0027" },
  { id: "usdt_bep20",   name: "USDT",  network: "BEP-20",  color: "#26a17b", logo: "/payment-logos/usdt.svg", min_usd: 5,  netColor: "#f3ba2f", netTextColor: "#1a1208" },
  { id: "usdt_erc20",   name: "USDT",  network: "ERC-20",  color: "#26a17b", logo: "/payment-logos/usdt.svg", min_usd: 15, netColor: "#627eea" },
  { id: "usdc_polygon", name: "USDC",  network: "Polygon", color: "#2775ca", logo: "/payment-logos/usdc.svg", min_usd: 5,  netColor: "#8247e5" },
  { id: "trx",          name: "TRX",   network: "TRC-20",  color: "#ef0027", logo: "/payment-logos/trx.svg",  min_usd: 10, netColor: "#ffffff", netTextColor: "#ef0027" },
  { id: "bnb",          name: "BNB",   network: "BEP-20",  color: "#f3ba2f", logo: "/payment-logos/bnb.svg",  min_usd: 5,  netColor: "#212121" },
  { id: "eth",          name: "ETH",   network: "ERC-20",  color: "#627eea", logo: "/payment-logos/eth.svg",  min_usd: 15, netColor: "#3c3c3d" },
  { id: "ton",          name: "TON",   network: "TON",     color: "#0098ea", logo: "/payment-logos/ton.svg",  min_usd: 5,  netColor: "#0098ea" },
];

function CryptoBadge({ color, letter, network, netColor, netTextColor = "#ffffff", logo, bg }) {
  if (logo) {
    return (
      <div
        className="relative h-10 w-10 rounded-xl flex items-center justify-center overflow-hidden shadow-md"
        style={{
          background: `linear-gradient(135deg, ${bg || color || "#19a55c"} 0%, ${(bg || color || "#19a55c")}cc 100%)`,
          padding: 7,
          boxShadow: `0 8px 18px -10px ${(bg || color || "#19a55c")}99, inset 0 1px 0 rgba(255,255,255,0.18)`,
        }}
        data-testid="crypto-badge-logo"
      >
        <img src={logo} alt="" className="h-full w-full object-contain drop-shadow" draggable={false} />
        {network && (
          <span
            className="absolute -bottom-[1px] -right-[1px] px-[3px] py-[1px] rounded-sm text-[7px] font-extrabold uppercase leading-[8px]"
            style={{ background: netColor || "#1f2937", color: netTextColor }}
          >
            {network}
          </span>
        )}
      </div>
    );
  }
  return (
    <div
      className="relative h-9 w-9 rounded-md grid place-items-center text-[14px] font-extrabold text-white overflow-hidden"
      style={{ backgroundColor: color }}
    >
      <span className="leading-none">{letter}</span>
      {network && (
        <span
          className="absolute -bottom-[1px] -right-[1px] px-[3px] py-[1px] rounded-sm text-[7px] font-extrabold uppercase leading-[8px]"
          style={{ background: netColor || "#1f2937", color: netTextColor }}
        >
          {network}
        </span>
      )}
    </div>
  );
}

function CryptoWithdrawForm({ disabled, allGatesOk, verificationOk, kycRequired, kycVerified, balance, symbol, onBack, onAfterSubmit }) {
  const [coinId, setCoinId] = useState("usdt_trc20");
  const [walletAddress, setWalletAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rate, setRate] = useState(120);
  const [enabledMap, setEnabledMap] = useState(null);    // {coinId: true}
  const [coinMins, setCoinMins] = useState({});           // {coinId: min_usd}
  const [autoCfg, setAutoCfg] = useState({ enabled: false, max_amount_bdt: 0 });

  const enabledCoins = enabledMap
    ? CRYPTO_COINS.filter((c) => enabledMap[c.id])
    : CRYPTO_COINS;
  const coin = enabledCoins.find((c) => c.id === coinId) || enabledCoins[0] || CRYPTO_COINS[0];

  useEffect(() => {
    api.get("/deposit-config").then((r) => {
      const conf = r.data || {};
      const c = conf.crypto || {};
      const r1 = Number(c.rate_bdt_per_usd || 120);
      setRate(r1 || 120);
      const coins = c.coins || {};
      const em = {};
      const mins = {};
      Object.entries(coins).forEach(([k, v]) => {
        em[k] = !!v?.enabled;
        if (v?.min_usd) mins[k] = Number(v.min_usd);
      });
      // If backend returned no coin config (legacy), enable all by default
      if (Object.keys(em).length === 0) CRYPTO_COINS.forEach((c) => { em[c.id] = true; });
      setEnabledMap(em);
      setCoinMins(mins);
      setAutoCfg(c.auto_withdraw || { enabled: false, max_amount_bdt: 0 });
      // Pick first enabled coin if current is disabled
      if (!em[coinId]) {
        const first = CRYPTO_COINS.find((c) => em[c.id]);
        if (first) setCoinId(first.id);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const amt = Number(amount) || 0;
  const minUsd = Number(coinMins[coin.id] ?? coin.min_usd ?? 5);
  const minBdt = Math.max(500, Math.round(minUsd * rate));
  const maxBdt = 100000;
  const usdAmount = rate > 0 ? +(amt / rate).toFixed(4) : 0;
  const willAuto = !!(autoCfg?.enabled && autoCfg.max_amount_bdt > 0 && amt > 0 && amt <= autoCfg.max_amount_bdt);

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return toast.error("Withdrawals are currently disabled");
    if (!verificationOk) return toast.error("Please verify your email or phone before withdrawing");
    if (kycRequired && !kycVerified) return toast.error(`KYC required for balance over ${symbol}50,000`);
    if (!coin) return toast.error("Select a coin");
    if (!walletAddress.trim()) return toast.error("Enter your wallet address");
    if (walletAddress.trim().length < 20) return toast.error("Wallet address looks invalid");
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt < minBdt) return toast.error(`Minimum for ${coin.name} (${coin.network}) is ${symbol}${minBdt.toLocaleString()}`);
    if (amt > maxBdt) return toast.error(`Maximum is ${symbol}${maxBdt.toLocaleString()}`);
    if (amt > balance) return toast.error("Insufficient balance");

    setSubmitting(true);
    try {
      await api.post("/profile/wallet/withdraw", {
        amount: amt,
        method: coin.id,
        wallet_address: walletAddress.trim(),
        crypto_network: coin.network.replace("-", ""),
      });
      await onAfterSubmit?.();
      toast.success(willAuto
        ? `${coin.name} withdrawal sent on-chain — funds arriving shortly`
        : `${coin.name} withdrawal request submitted — pending admin approval`);
      setAmount(""); setWalletAddress("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Withdrawal failed");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 bg-[#0e1622] border border-white/8 rounded-2xl p-4" data-testid="crypto-withdraw-form">
      <BackHeader title="Crypto Withdraw" sub="USDT / USDC / TRX / BNB / ETH / TON" onBack={onBack} icon={Bitcoin} color="#f59e0b" />

      {willAuto && (
        <div className="rounded-xl px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[12px] flex items-center gap-2" data-testid="auto-withdraw-badge">
          <Sparkles size={12} className="text-emerald-300" />
          <span><strong>Instant on-chain payout</strong> — this amount qualifies for automatic processing.</span>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-wider text-white/55 mb-2">Select Coin & Network</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {enabledCoins.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCoinId(c.id)}
              data-testid={`crypto-coin-${c.id}`}
              className={`h-14 rounded-lg border text-left px-3 flex items-center gap-2 transition-all ${
                coinId === c.id
                  ? "border-amber-400 bg-amber-500/10 text-white"
                  : "border-white/10 bg-[#101824] text-white/75 hover:bg-white/[0.04]"
              }`}
            >
              <CryptoBadge logo={c.logo} bg={c.color} network={c.network.replace("-", "")} netColor={c.netColor} netTextColor={c.netTextColor} />
              <div className="min-w-0">
                <div className="text-[13px] font-bold leading-tight">{c.name}</div>
                <div className="text-[10.5px] text-white/55 leading-tight">{c.network}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="text-xs uppercase tracking-wider text-white/55">{coin.name} ({coin.network}) Wallet Address</span>
        <input
          type="text"
          data-testid="crypto-wallet-address"
          value={walletAddress}
          onChange={(e) => setWalletAddress(e.target.value)}
          placeholder={`Paste your ${coin.network} wallet address`}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          className="mt-1 w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-white placeholder:text-white/30 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/25 font-mono text-[13px]"
        />
        <div className="mt-1.5 text-[11px] text-amber-300/80 flex items-start gap-1.5">
          <AlertCircle size={11} className="mt-0.5 shrink-0" />
          <span>Double-check the address. Sending to a wrong address or wrong network will result in permanent loss of funds.</span>
        </div>
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-wider text-white/55 flex items-center justify-between">
          <span>Amount (BDT)</span>
          <span className="text-white/40 normal-case tracking-normal text-[11px]">Min {symbol}{minBdt.toLocaleString()} · Max {symbol}{maxBdt.toLocaleString()}</span>
        </span>
        <input
          type="number"
          inputMode="decimal"
          data-testid="crypto-withdraw-amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={`Enter amount in ${symbol}`}
          className="mt-1 w-full h-12 rounded-lg bg-[#101824] border border-white/10 px-4 text-white text-lg font-bold tabular-nums focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/25"
        />
      </label>

      {amt > 0 && (
        <div data-testid="crypto-conversion-preview" className="rounded-xl px-3 py-3 bg-amber-500/[0.06] border border-amber-500/20 text-sm">
          <div className="flex items-center justify-between text-white/70">
            <span>You send</span>
            <span className="font-semibold text-white">{symbol}{amt.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between mt-1.5 text-white/70">
            <span>You receive (≈)</span>
            <span className="font-bold text-amber-300 tabular-nums">{usdAmount} {coin.name}</span>
          </div>
          <div className="text-[10.5px] text-white/40 mt-1.5">Rate: 1 USD = {symbol}{rate}. Exact amount confirmed at admin approval.</div>
        </div>
      )}

      <button
        type="submit"
        data-testid="crypto-withdraw-submit"
        disabled={submitting || disabled || !allGatesOk}
        className="w-full h-12 rounded-lg text-white font-extrabold tracking-wide border border-amber-400/40 bg-gradient-to-r from-amber-500/20 to-amber-500/10 hover:from-amber-500/30 hover:to-amber-500/20 active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? "Submitting…" : !verificationOk ? "Verify email or phone first" : kycRequired && !kycVerified ? "Complete KYC to withdraw" : `Request ${coin.name} withdrawal`}
      </button>
    </form>
  );
}


/**
 * SearchableSelect — lightweight, dark-themed dropdown with a search input.
 * Replaces native <select> for cases where the user might want to type-to-filter
 * (e.g. 64 BD districts, long agent state-address list).
 *
 * Props:
 *  - options: array of items
 *  - value: currently-selected option value
 *  - onChange(value): called when user picks
 *  - getValue(o): returns the value to compare against `value` (default: o.id || o.value)
 *  - getLabel(o): main display label
 *  - getSub(o): optional secondary line shown under label
 *  - getSearchText(o): string used for matching (default: label + sub)
 *  - placeholder: button text when nothing selected
 *  - searchPlaceholder: placeholder for search input
 *  - emptyText: shown when no options match
 *  - leftIcon: lucide icon component shown inside the trigger
 *  - disabled: boolean
 *  - testid, listTestid, searchTestid
 *  - itemTestidPrefix: option rows get `${prefix}-${value}` test ids
 *  - renderRow(o, isActive): optional custom row renderer
 */
function SearchableSelect({
  options = [],
  value,
  onChange,
  getValue = (o) => o?.id ?? o?.value,
  getLabel = (o) => o?.label ?? o?.name ?? "",
  getSub,
  getSearchText,
  placeholder = "Select…",
  searchPlaceholder = "Type to search…",
  emptyText = "No matches",
  leftIcon: LeftIcon,
  disabled = false,
  testid,
  listTestid,
  searchTestid,
  itemTestidPrefix,
  renderRow,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // focus search input when opening
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    const onClickAway = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Reset filter every time the dropdown closes
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const selected = options.find((o) => getValue(o) === value) || null;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => {
      const txt = (
        (typeof getSearchText === "function" ? getSearchText(o) : "") ||
        `${getLabel(o) || ""} ${typeof getSub === "function" ? (getSub(o) || "") : ""}`
      ).toLowerCase();
      return txt.includes(needle);
    });
  }, [q, options, getLabel, getSub, getSearchText]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className={`appearance-none w-full h-12 rounded-xl bg-[#0d1521] border border-white/10 ${LeftIcon ? "pl-11" : "pl-4"} pr-9 text-white text-[14px] font-semibold focus:border-amber-500/60 focus:outline-none focus:ring-2 focus:ring-amber-500/15 transition cursor-pointer hover:bg-[#101824] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#0d1521] text-left flex items-center`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {LeftIcon && (
          <LeftIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-amber-400/70 pointer-events-none" />
        )}
        <span className={`flex-1 truncate ${selected ? "text-white" : "text-white/40 font-normal"}`}>
          {selected ? getLabel(selected) : placeholder}
        </span>
        <ChevronRight size={15} className={`absolute right-3 top-1/2 -translate-y-1/2 text-white/35 transition-transform ${open ? "-rotate-90" : "rotate-90"}`} />
      </button>

      {open && (
        <div
          data-testid={listTestid}
          className="absolute z-30 left-0 right-0 mt-1.5 rounded-xl bg-[#0d1521] border border-amber-500/30 shadow-[0_24px_48px_-16px_rgba(0,0,0,0.7)] overflow-hidden"
          role="listbox"
        >
          {/* Search input */}
          <div className="relative px-2 pt-2 pb-2 border-b border-white/5">
            <Search size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              data-testid={searchTestid}
              className="w-full h-9 rounded-lg bg-[#101a28] border border-white/8 pl-8 pr-8 text-white text-[13px] placeholder-white/30 focus:border-amber-500/40 focus:outline-none focus:ring-1 focus:ring-amber-500/20"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80"
                aria-label="Clear search"
              >
                <XIcon size={13} />
              </button>
            )}
          </div>
          {/* Options list */}
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-[12.5px] text-white/40">{emptyText}</div>
            ) : (
              filtered.map((o) => {
                const v = getValue(o);
                const isActive = v === value;
                if (typeof renderRow === "function") {
                  return (
                    <div
                      key={String(v)}
                      role="option"
                      aria-selected={isActive}
                      data-testid={itemTestidPrefix ? `${itemTestidPrefix}-${v}` : undefined}
                      onClick={() => {
                        onChange(v);
                        setOpen(false);
                      }}
                      className={`cursor-pointer transition-colors ${isActive ? "bg-amber-500/15" : "hover:bg-white/5"}`}
                    >
                      {renderRow(o, isActive)}
                    </div>
                  );
                }
                return (
                  <button
                    type="button"
                    key={String(v)}
                    role="option"
                    aria-selected={isActive}
                    data-testid={itemTestidPrefix ? `${itemTestidPrefix}-${v}` : undefined}
                    onClick={() => {
                      onChange(v);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 transition-colors flex items-start gap-2 ${isActive ? "bg-amber-500/15 text-amber-200" : "hover:bg-white/5 text-white/85"}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold truncate">{getLabel(o)}</div>
                      {typeof getSub === "function" && getSub(o) ? (
                        <div className="text-[11px] text-white/45 truncate mt-0.5">{getSub(o)}</div>
                      ) : null}
                    </div>
                    {isActive && <CheckCircle2 size={14} className="text-amber-300 mt-0.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
